/*
    This will be the Live Track page 

    You can see the table for pass times / estimated times etc

    There will be a boolean passed for editing - so this component will be able to be used for both editing and viewing only
        -May have to configure user context with this as well for permissions
    
    Basically the same as old VM

    This is the non editing version of the live track table

    using a hook for consistant polling from the database so any new data comes in and added to table component
*/

//test data
// import {agmData, scData} from '../testAgm';

import { useEffect, useRef, useState } from "react";
//components
import LiveTrackTable from "../components/tables/LiveTrackTable";
import { PageContainer } from "../components/ui/layout/PageContainer";
import { PageHeader } from "../components/ui/layout/PageHeader";
import Label from "../components/ui/labels/Label";
import { SelectContainer } from "../components/ui/layout/SelectContainer";
import Select from "react-select";
import { LiveTrackSummary } from "../components/liveTrack/LiveTrackSummary";
import { RunMessages } from "../components/messages/RunMessages";
//hooks
import useJobOptions from "../hooks/useJobOptions";
import useRunOptions from "../hooks/useRunOptions";
import useLiveTrackData from "../hooks/useLiveTrackData";
import { useUser } from "@contexts/UserContext";
import { useError } from "@contexts/ErrorContext";
//context
import { RunMessagesProvider } from "../contexts/runMessages/RunMessageContext";
//utils
import { fetchJobData } from "@utils/jobs/fetchJobData";

/**
 * User can view live data from an active run
 * 
 * @returns {JSX.Element}
 * 
 * @category Pages
 */
export const LiveTrack = () => {
  const { jobNameOptions } = useJobOptions();
  const { runNameOptions, fetchRuns, clearRuns } = useRunOptions();
  const { storedJob, setStoredJob, userPermissions } = useUser()
  const {showError} = useError()
    
  const hasJobPermissions = userPermissions.jobs.create && userPermissions.jobs.update

  const {
    formattedLiveTrackTable,
    fetchLiveTrackData,
    checkLiveTrackForUpdates,
    findAndStoreLastUpdateValue,
    runData,
    runMessageCount,
    notificationsPaused,
    setNotificationsPaused
  } = useLiveTrackData();

  const [selectedJobOption, setSelectedJobOption] = useState(null);
  const [selectedRunOption, setSelectedRunOption] = useState(null);
  const [dataUpdated, setDataUpdated] = useState() //update every 10s interval so live track summary updates in sync
  const hasFetchedUpdatesRef = useRef(false);

  const jobSelectedGetRuns = (job) => {
    fetchJobData(job, showError, setSelectedJobOption, setStoredJob)
    clearRuns();
    setSelectedRunOption(null);
  };

  useEffect(() => {
    if (selectedJobOption) {
      fetchRuns(selectedJobOption.value.toString());
    }
  }, [selectedJobOption]); //eslint-disable-line

  const runSelectedGetLiveTrackData = (run) => {
    setSelectedRunOption(run);
  };

  useEffect(() => {
    if (selectedRunOption) {
      // getRunData();
      fetchLiveTrackData(selectedRunOption.value);
      hasFetchedUpdatesRef.current = false;
    }
  }, [selectedRunOption]); //eslint-disable-line

  useEffect(() => {
    findAndStoreLastUpdateValue();
  }, [formattedLiveTrackTable]); //eslint-disable-line

  useEffect(() => {
    if (!selectedRunOption || formattedLiveTrackTable.length === 0 || hasFetchedUpdatesRef.current) return;

    // Initial fetch so components relying on run data / message counts render immediately.
    checkLiveTrackForUpdates(selectedRunOption.value.toString());
    setDataUpdated(new Date());

    hasFetchedUpdatesRef.current = true;
  }, [selectedRunOption, formattedLiveTrackTable]); //eslint-disable-line

  //interval for time calls to API for updated data
  useEffect(() => {
    if (!selectedRunOption) return;

    const interval = setInterval(() => {
      checkLiveTrackForUpdates(selectedRunOption.value.toString());
      setDataUpdated(new Date()); //let summary component know it should update as well
    }, 10000); // 10000 milliseconds = 10 seconds

    // Cleanup function to clear the interval when the component unmounts or the selectedRun changes
    return () => {
      clearInterval(interval);
    };
  }, [selectedRunOption]); //eslint-disable-line

  useEffect(() => {
    document.title = `MarTrak - Live Track`;
    
    //if a job was previously selected and added to UserContext, pull it in here
    if(storedJob){
      fetchJobData(storedJob, showError, setSelectedJobOption, setStoredJob)
    }
  }, []); //eslint-disable-line

  return (
    <>
      <PageHeader pageTitle="Live Track" />

      <PageContainer>
        <Label>Select A Job</Label>

        <SelectContainer>
          <Select
            options={jobNameOptions}
            onChange={jobSelectedGetRuns}
            styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
            menuPosition="fixed"
            value={selectedJobOption}
          />
        </SelectContainer>

        <Label>Select A Run</Label>

        <SelectContainer>
          <Select
            options={runNameOptions}
            onChange={runSelectedGetLiveTrackData}
            styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
            menuPosition="fixed"
            value={selectedRunOption}
          />
        </SelectContainer>

        {/* show live track data if run selected and launched */}
        {
          // check for job and run
          selectedJobOption && selectedRunOption ? (
            <>
              {/* if job IsLocked is set to false, or property is null (from old jobs), don't show table */}
              {selectedJobOption.IsLocked === false ? (
                <div className='flex justify-center mt-10'>
                    <p className='text-purple-600 font-bold italic'>
                      {hasJobPermissions ? 
                      'Job is unlocked. To view this page, go to Job Details and lock the job.' : 
                      'Job is still being set up. Please check back later.'}
                    </p>
                </div>
              ) : (
                <>
                        
                  {/* can show table if we have a job and run selected, job is locked (or IsLocked is null from old job) */}
                  {/* need run data to show summary and messages */}
                  <div className="grid grid-cols-1 gap-1 md:grid-cols-2 my-10">
                    { 
                      runData && 
                      runData.HasLaunched &&
                      (
                        // run messages area inside of RunMessages context, in order to share messages state
                          <RunMessagesProvider
                            setNotificationsPaused={setNotificationsPaused}
                          >
                              <LiveTrackSummary 
                                  selectedJob={selectedJobOption.value}
                                  selectedRun={selectedRunOption.value}
                                  dataUpdated={dataUpdated}
                                  timezone={selectedJobOption.timezone}
                              />
                  
                              <div>
                                  <RunMessages
                                      selectedRun={selectedRunOption.value} 
                                      numberOfMessages={runMessageCount}
                                      dataUpdated={dataUpdated}
                                      notificationsPaused={notificationsPaused}
                                      timezone={selectedJobOption.timezone}
                                  />
                  
                              </div>
                                              
                          </RunMessagesProvider>
                      )
                    }
                  </div>

                  <LiveTrackTable
                    agmData={formattedLiveTrackTable}
                    isEditable={false}
                    timezone={selectedJobOption.timezone}
                    jobName={selectedJobOption.label}
                    runName={selectedRunOption.label}
                    notificationsPaused={notificationsPaused}
                  />
                </>
              )}
            </>
          ) : null
        }
        
      </PageContainer>
    </>
  );
};
